import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ShieldCheck } from "lucide-react";
import { LogoHorizontal } from "@/components/Logo";
import { toast } from "sonner";
import { SURFACE } from "@/lib/surface";

export default function Login({ adminOnly = false }) {
  const isAdminLogin = adminOnly || SURFACE === "admin";
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const [username, setU] = useState("");
  const [password, setP] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(username, password);
      if (isAdminLogin) {
        if (u.role !== "admin" && u.role !== "staff") {
          toast.error("Tài khoản này không có quyền quản trị");
          setBusy(false);
          return;
        }
        toast.success(`Chào mừng ${u.nickname}`);
        nav("/admin");
        return;
      }
      toast.success(`Chào mừng ${u.nickname}`);
      if (next) nav(next);
      else nav(u.role === "admin" || u.role === "staff" ? "/admin" : "/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Đăng nhập thất bại");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#05050a]">
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full bg-purple-500/10 blur-3xl" />

      <div className="relative max-w-md mx-auto px-6 py-20">
        <LogoHorizontal size={56} className="mb-10" />

        {isAdminLogin ? (
          <>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-300 text-[11px] uppercase tracking-widest mb-3">
              <ShieldCheck size={12} /> Khu vực quản trị
            </div>
            <h1 className="font-display text-3xl font-bold mb-2">Đăng nhập Admin</h1>
            <p className="text-white/50 text-sm mb-8">Chỉ dành cho quản trị viên & nhân viên.</p>
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold mb-2">Đăng nhập</h1>
            <p className="text-white/50 text-sm mb-8">Chiến đấu. Leo rank. Trở thành Chiến Thần.</p>
          </>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Tên đăng nhập</label>
            <input
              data-testid="login-username"
              value={username}
              onChange={(e) => setU(e.target.value)}
              required
              autoComplete="username"
              className="w-full bg-white/[0.03] border border-white/10 focus:border-cyan-400/60 focus:bg-white/[0.05] outline-none rounded-md px-4 py-3 text-sm transition"
              placeholder="admin"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Mật khẩu</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setP(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full bg-white/[0.03] border border-white/10 focus:border-cyan-400/60 focus:bg-white/[0.05] outline-none rounded-md px-4 py-3 text-sm transition"
              placeholder="••••••••"
            />
          </div>

          <button
            data-testid="login-submit"
            disabled={busy}
            className="w-full mt-2 py-3 rounded-md font-display font-semibold uppercase tracking-wider text-sm bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 text-black hover:opacity-90 disabled:opacity-50 transition flex items-center justify-center gap-2"
          >
            {busy && <Loader2 size={16} className="animate-spin" />} Vào trận
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-white/50">
          {isAdminLogin ? (
            <span className="text-white/30">Người chơi vui lòng quay về app chính</span>
          ) : (
            <>Chưa có tài khoản? <Link to="/register" className="text-amber-400 hover:underline" data-testid="goto-register">Đăng ký</Link></>
          )}
        </div>
      </div>
    </div>
  );
}
